import { type AuthorInfo, getHeroImageUrl, type SearchArticleMeta } from "@serophin/nottldr-shared";
import { AiBadge } from "../author/AiBadge";
import { AuthorProfileImage } from "../author/AuthorProfileImage";
import { FollowButton } from "../author/FollowButton";
import { ArticleActionsMenu } from "./ArticleActionsMenu";
import { AudioPlayerControls } from "./AudioPlayerControls";
import { fallbackImage } from "./imageFallback";

type HeaderProps = { meta: SearchArticleMeta; author: AuthorInfo };

/** The play controls and the menu, in the mobile header (see the page's `useHeaderActions`). */
export const HeaderControls = ({ meta }: { meta: SearchArticleMeta }) => (
  <div class="flex items-center h-full gap-1">
    <AudioPlayerControls
      authorAlias={meta.authorAlias}
      articleId={meta.id}
      articleTitle={meta.title}
      showFullControls={true}
      class="hover:bg-gray-100/10 dark:hover:bg-gray-700/20"
      iconClass="text-gray-700 dark:text-gray-300"
    />
    <ArticleActionsMenu meta={meta} class="hover:bg-gray-100/10 dark:hover:bg-gray-700/20" iconClass="text-gray-700 dark:text-gray-300" />
  </div>
);

/** The hero picture with the title over it; on a wide screen the controls sit in its corner. */
export const ArticleHero = ({ meta }: { meta: SearchArticleMeta }) => (
  <header class="relative">
    <img
      src={getHeroImageUrl(meta)}
      alt={meta.image.alt || meta.title}
      class="w-full aspect-[1/1] object-cover rounded-t-lg"
      fetchpriority="high"
      onError={fallbackImage(800, 800, meta.publishedAt)}
    />
    <div class="hidden lg:flex absolute top-3 right-3 items-start gap-1 z-10">
      <AudioPlayerControls
        authorAlias={meta.authorAlias}
        articleId={meta.id}
        articleTitle={meta.title}
        showFullControls={true}
        overlayMode={true}
        class="bg-black/50 hover:bg-black/75 backdrop-blur-sm"
        iconClass="text-white"
      />
      <ArticleActionsMenu meta={meta} backgroundClass="bg-black/50 hover:bg-black/75 backdrop-blur-sm" iconClass="text-white" />
    </div>
    <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3 md:rounded-t-lg">
      <h1 class="text-2xl font-bold text-white">{meta.title}</h1>
    </div>
  </header>
);

export const Attribution = ({ html }: { html: string }) => (
  <div class="px-4 py-0 text-gray-500 bg-white dark:bg-neutral-800 text-xs text-center">
    <small class="unsplash-attribution" dangerouslySetInnerHTML={{ __html: html.replaceAll("concisely", "nottldr") }} />
  </div>
);

export const ByLine = ({ meta, author }: HeaderProps) => (
  <div class="flex items-center justify-between text-gray-600 dark:text-gray-300 text-sm mb-4">
    <div class="flex items-center">
      <AuthorProfileImage author={author} size="sm" />
      <div class="ml-2">
        <div class="flex items-center gap-1.5">
          <a href={`/authors/${author.alias}`} class="hover:underline">
            {author.alias}
          </a>
          <AiBadge class="text-gray-500 dark:text-gray-400" />
        </div>
        <div class="text-xs text-gray-500 dark:text-gray-400">AI-generated · {meta.readingMinutes} min read</div>
      </div>
    </div>
    <div class="flex items-center space-x-2">
      <FollowButton alias={author.alias} display="button" />
    </div>
  </div>
);
